import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { canvasesApi } from '@/api/endpoints/canvases';
import { humanizeError, isApiError } from '@/api/errors';
import { track } from '@/analytics/tracker';
import { ApertureMark } from '@/components/ui/ApertureMark';
import styles from '@/features/canvas/Canvas.module.css';
import { useT } from '@/i18n/LocaleProvider';
import { canvasesQueryKey } from '@/library/useCanvases';

/**
 * `/invites/:token` из письма-приглашения (canvas-spec.md §7): принимаем и ведём
 * на канвас. Auth-гейт показывает вход на этом же адресе, так что ссылка
 * переживает регистрацию.
 */
export function InviteRoute() {
  const { token } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useT();
  const [error, setError] = useState<string | null>(null);

  // Один вызов на токен: в dev-режиме React запускает эффект дважды, а второй
  // запрос уже не должен ни дублироваться, ни перекрывать успех первого.
  const started = useRef<string | null>(null);
  useEffect(() => {
    if (!token || started.current === token) return;
    started.current = token;
    canvasesApi
      .acceptInvite(token)
      .then((response) => {
        void queryClient.invalidateQueries({ queryKey: canvasesQueryKey });
        track('canvas_participant_joined', {
          metadata: { canvas_id: response.data.canvas_id, role: response.data.role, via: 'invite' },
        });
        navigate(`/canvases/${response.data.canvas_id}`, { replace: true });
      })
      .catch((e: unknown) => {
        if (isApiError(e) && e.code === 'invite_email_mismatch') setError(t('invite.mismatch'));
        else if (isApiError(e) && e.status === 404) setError(t('invite.notFound'));
        else setError(humanizeError(e));
      });
  }, [token, navigate, queryClient, t]);

  return (
    <div className={styles.state} style={{ minHeight: 'calc(100vh - var(--app-header-h))' }}>
      {error ? (
        <div role="alert">
          {error}
          <div>
            <Link
              to="/"
              className={styles.ghost}
              style={{ marginTop: 12, display: 'inline-block' }}
            >
              {t('shared.goToCatalog')}
            </Link>
          </div>
        </div>
      ) : (
        <div className={styles.loader} role="status" aria-live="polite">
          <ApertureMark className={styles.loaderMark} />
          <span className={styles.stateLabel}>{t('invite.accepting')}</span>
        </div>
      )}
    </div>
  );
}
